import React from "react"
// aldoo-ra/AldooComponent/reader.js
// Use Vite's glob import for component auto-discovery.
// WHITELIST, not a broad glob: an eager import of every aldoo-ra index.jsx
// bundled the whole framework (admin panels, editors) into every page while
// only the modules below actually export an AldooComponent definition.
// A new aldoo-ra module that registers an AldooComponent must be added here.
const defaultComponents = import.meta.glob(
  [
    "/aldoo-ra/HTMLContent/index.jsx",
    "/aldoo-ra/AldooVike/templates/components/*/index.jsx",
  ],
  {
    eager: true,
  }
)

// Project components: one small sidecar per CMS component —
// components/<Name>/aldoo-component.{js,jsx} exporting AldooComponent.
// The previous eager glob of every /components/**/*.jsx bundled the whole
// project into the entry chunk (3.6 MB for meet) whether or not a file
// defined a component; lazy routes could not split anything. A sidecar may
// point at a heavy component with `lazy: () => import("./index.jsx")`
// instead of `component`, and the component then loads on first render.
const userComponents = import.meta.glob("/components/**/aldoo-component.{js,jsx}", {
  eager: true,
})

// A definition with `lazy` gets a component that fetches the real one on
// first render (React.lazy inside its own Suspense boundary, fallback null
// — CMS content, not a route, so a brief blank is fine).
const materialize = (definition) => {
  if (definition.component || typeof definition.lazy !== "function") return definition
  const Lazy = React.lazy(() => definition.lazy().then((m) => ({ default: m.default || m[definition.id] })))
  const Wrapped = (props) => React.createElement(React.Suspense, { fallback: null }, React.createElement(Lazy, props))
  Wrapped.displayName = `Lazy(${definition.id})`
  return { ...definition, component: Wrapped }
}

// Debug: uncomment to see discovered files
// console.log('🔍 AldooComponent: Auto-discovery initialized')
// console.log('📁 Found default component files:', Object.keys(defaultComponents))
// console.log('📁 Found user component files:', Object.keys(userComponents))

export class ComponentReader {
  static #componentCache = new Map()
  static initialized = false
  static #initializing = false

  static init() {
    if (this.initialized || this.#initializing) return

    this.#initializing = true

    // Auto-discover components from aldoo-ra modules
    Object.entries(defaultComponents).forEach(([path, module]) => {
      if (module.AldooComponent && typeof module.AldooComponent === 'object') {
        const definition = materialize(module.AldooComponent)
        if (definition.id && definition.component) {
          this.#componentCache.set(definition.id, definition)
        }
      }
    })

    // Auto-discover components from user components
    Object.entries(userComponents).forEach(([path, module]) => {
      if (module.AldooComponent && typeof module.AldooComponent === 'object') {
        const definition = materialize(module.AldooComponent)
        if (definition.id && definition.component) {
          this.#componentCache.set(definition.id, definition)
        }
      }
    })

    this.#initializing = false
    this.initialized = true
  }

  static registerComponent(definition) {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    if (!definition.id || !definition.component) {
      throw new Error('Component definition must have both id and component properties')
    }

    this.#componentCache.set(definition.id, definition)
  }

  static getComponent(id) {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    return this.#componentCache.get(id)
  }

  static getComponents() {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    return Array.from(this.#componentCache.values())
  }

  static getRegistry() {
    if (!this.initialized && !this.#initializing) {
      this.init()
    }

    return this.#componentCache
  }

  static clearCache() {
    this.#componentCache.clear()
    this.initialized = false
    this.#initializing = false
  }
}